import { InventoryGeometryItem } from "../../../hooks/database/types/inventories";
import { useMap } from "../../../App";
import { useCallback, useEffect, useRef } from "react";
import ol from "openlayers";
import { useMapProjection } from "../../../hooks/useMapView";
import { useSetMapClickEnabled } from "../../../state";

const editStyle = new ol.style.Style({
  fill: new ol.style.Fill({
    color: "rgba(120, 120, 255, 0.4)",
  }),
  stroke: new ol.style.Stroke({
    color: "rgba(0, 38, 255, 0.4)",
    width: 14,
  }),
  image: new ol.style.Circle({
    radius: 8,
    stroke: new ol.style.Stroke({
      color: "rgba(100, 100, 255, 0.6)",
      width: 2,
    }),
    fill: new ol.style.Fill({
      color: "rgba(120, 120, 255, 0.8)",
    }),
  }),
});

export function useBaseGeometryController(onSave: (geojson: InventoryGeometryItem["geom"]) => void) {
  const map = useMap()!;

  // create a source and add it to the map in a layer
  const drawSourceRef = useRef<ol.source.Vector>(new ol.source.Vector({ wrapX: false }));
  useEffect(() => {
    const layer = new ol.layer.Vector({
      source: drawSourceRef.current,
      style: editStyle,
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    });
    map.addLayer(layer);
    return () => {
      map.removeLayer(layer);
    };
  }, [map]);

  const projection = useMapProjection();
  const saveGeometry = useCallback(
    (geometry: ol.geom.Geometry) => {
      const geojson = new ol.format.GeoJSON().writeGeometryObject(geometry, {
        featureProjection: projection,
        dataProjection: "EPSG:4326",
      }) as unknown as InventoryGeometryItem["geom"]["geometry"];
      onSave({ geometry: geojson, type: "Feature", properties: { name: "", id: "" } });
    },
    [projection, onSave]
  );

  // map clicks are disabled while creating/modifying geometries
  const setMapClickEnabled = useSetMapClickEnabled();
  useEffect(() => {
    setMapClickEnabled(false);
    return () => setMapClickEnabled(true);
  }, [setMapClickEnabled]);

  return { drawSource: drawSourceRef.current, saveGeometry };
}
